import Swal from 'sweetalert2';
import { notificationApi } from '../api/notification.api';
import type { NotificationApiResponse } from '@/shared/lib';
import type { NotificationSearchType } from './types';

/**
 * Notification utilities composable
 * Provides utility functions for formatting and displaying notifications
 */
export function useNotification() {
  const getInitial = (name: string) => {
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  const getRelativeTime = (dateString: string) => {
    const notificationDate = new Date(dateString).getTime();
    const now = Date.now();
    const diffMs = now - notificationDate;

    if (diffMs < 0) return 'agora';

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `Há ${days} dia${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `Há ${hours} hora${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `Há ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    } else {
      return 'agora';
    }
  };

  const openDocument = (documento: string) => {
    Swal.fire({
      title: 'Documento',
      text: documento,
      icon: 'success',
      confirmButtonText: 'Fechar',
    });
  };

  const fetchNotification = async (params?: {
    page?: number;
    itemsPerPage?: number;
    term?: string;
    sort?: string;
    order?: string;
    searchType?: NotificationSearchType;
    createdBy?: number;
    status?: string;
    periodicity?: string;
    startDate?: string;
    endDate?: string;
    customPeriodicity?: number;
    customPeriodicityUnit?: string;
  }): Promise<NotificationApiResponse> => {
    try {
      return await notificationApi.getNotifications(params);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      throw error;
    }
  };

  const deleteNotification = async (id: number): Promise<void> => {
    try {
      await notificationApi.deleteNotification(id);
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
      throw error;
    }
  };

  const bulkDeleteNotifications = async (
    ids: number[]
  ): Promise<{
    totalRequested: number;
    successCount: number;
    failureCount: number;
    succeededIds: number[];
    failedOperations: Array<{ id: number; error: string }>;
    invalidIds: number[];
  }> => {
    try {
      return await notificationApi.bulkDeleteNotifications(ids);
    } catch (error) {
      console.error('Erro ao deletar notificações em lote:', error);
      throw error;
    }
  };

  return {
    getInitial,
    getRelativeTime,
    openDocument,
    // Re-export API functions for backward compatibility
    fetchNotification,
    deleteNotification,
    bulkDeleteNotifications,
  };
}
